import { useEffect, useRef, useState } from 'react';

import { useProjectStore } from '@stores/project.store';
import type { ProjectListItem } from '@services/projects.service';

import { useProjects } from './useProjects';

/**
 * Combines the ProjectStore selection with the live projects list.
 *
 * Reconciliation rules — these run on every projects-list change so that
 * cross-tab edits and Batch-B hard-deletes can't leave the store
 * pointing at a project that no longer exists:
 *
 * 1. selectedProjectId is null AND no auto-select has happened yet AND
 *    exactly 1 project exists → auto-select it. (Once-only via a ref so
 *    that explicit `setSelected(null)` from the user is not undone.)
 * 2. selectedProjectId points to a project that is not in the list →
 *    fall back to the first project, or null if none.
 *
 * `isReady` is true once both conditions hold: projects have been
 * fetched AND reconciliation has had a chance to run. Consumers that
 * gate UI on a "the store is consistent with the server" guarantee
 * should wait on `isReady`, not on `!isLoading` alone.
 */
export function useActiveProject(): {
  selectedProjectId: string | null;
  selectedProject: ProjectListItem | null;
  setSelected: (id: string | null) => void;
  isReady: boolean;
} {
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const setSelected = useProjectStore((s) => s.setSelected);
  const { projects, isLoading } = useProjects();

  // Gate Rule 1 so explicit `setSelected(null)` is not re-clobbered, but
  // let Rule 2 run on every list change.
  const autoSelected = useRef(false);
  const [reconciliationRun, setReconciliationRun] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    // If the user has ever expressed a selection (including in a prior
    // session — selectedProjectId is hydrated from localStorage), lock
    // Rule 1 permanently. This is what keeps `setSelected(null)` from
    // being immediately undone by the auto-select branch below.
    if (selectedProjectId !== null) {
      autoSelected.current = true;
    }

    // Rule 1: auto-select the sole project the very first time we see
    // a list that contains exactly one project and the user has never
    // expressed a preference.
    if (
      !autoSelected.current &&
      selectedProjectId === null &&
      projects.length === 1
    ) {
      autoSelected.current = true;
      setSelected(projects[0]!.id);
      setReconciliationRun(true);
      return;
    }

    // Rule 2: stale-id fallback. Runs every time the list changes.
    if (selectedProjectId !== null) {
      const stillExists = projects.some((p) => p.id === selectedProjectId);
      if (!stillExists) {
        setSelected(projects.length > 0 ? projects[0]!.id : null);
      }
    }

    setReconciliationRun(true);
  }, [isLoading, projects, selectedProjectId, setSelected]);

  const selectedProject =
    selectedProjectId !== null
      ? (projects.find((p) => p.id === selectedProjectId) ?? null)
      : null;

  return {
    selectedProjectId,
    selectedProject,
    setSelected,
    isReady: !isLoading && reconciliationRun,
  };
}
